import { supabase } from '../lib/supabase'
import type {
  AutoAssignmentPreview,
  BrokerComercialAssignment,
  BrokerComercialGestaoItem,
  BrokerComercialRule,
  BrokerComercialRuleInput,
  ComercialUser,
  ManualAssignmentPayload,
} from '../types/comercial'

const MANUAL_ONLY_CATEGORIES = new Set(['START', 'BASIC'])
const RULE_VERSION = 'v2_regras_dinamicas'
const SUPABASE_PAGE_SIZE = 1000

type BrokerRow = {
  id: string
  name: string
  id_scgp: number | null
  classification: string | null
  state: string | null
  city: string | null
}

const IN_CHUNK_SIZE = 200

type RuleLike = Pick<
  BrokerComercialRuleInput,
  'categories' | 'states' | 'is_fallback' | 'priority' | 'comercial_user_id' | 'is_active'
>

function normalize(value?: string | null) {
  return (value || '').trim()
}

function isMissingRelationError(error: any): boolean {
  return error?.code === '42P01' || String(error?.message || '').toLowerCase().includes('does not exist')
}

function isManualOnlyCategory(classification: string | null): boolean {
  return MANUAL_ONLY_CATEGORIES.has(normalize(classification).toUpperCase())
}

function matchesRule(item: BrokerComercialGestaoItem, rule: RuleLike): boolean {
  if (!rule.is_active) return false
  const category = normalize(item.classification).toUpperCase()
  const uf = normalize(item.state).toUpperCase()
  const ruleCategories = (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean)
  const ruleStates = (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean)

  if (ruleCategories.length === 0) return false
  if (!ruleCategories.includes(category)) return false
  if (rule.is_fallback) return true
  if (ruleStates.length === 0) return true
  return ruleStates.includes(uf)
}

function getMatchedRule(item: BrokerComercialGestaoItem, rules: RuleLike[]): RuleLike | null {
  for (const rule of rules) {
    if (matchesRule(item, rule)) return rule
  }
  return null
}

async function fetchAllPages<T>(
  buildQuery: (from: number, to: number) => any
): Promise<T[]> {
  const all: T[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const to = from + SUPABASE_PAGE_SIZE - 1
    const { data, error } = await buildQuery(from, to)
    if (error) throw error

    const page = (data || []) as T[]
    all.push(...page)

    if (page.length < SUPABASE_PAGE_SIZE) {
      hasMore = false
    } else {
      from += SUPABASE_PAGE_SIZE
    }
  }

  return all
}

export const comercialGestaoService = {
  async listAvailableCategories(): Promise<string[]> {
    const { data, error } = await supabase
      .from('dashcorretor_classifications')
      .select('name')
      .order('name', { ascending: true })

    if (error) throw error

    return Array.from(
      new Set(
        ((data || []) as Array<{ name: string | null }>)
          .map((row) => normalize(row.name).toUpperCase())
          .filter(Boolean)
      )
    )
  },

  async listComercialUsers(): Promise<ComercialUser[]> {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, name, is_active')
      .eq('is_active', true)
      .order('name')

    if (error) throw error

    return ((data || []) as Array<{ id: string; name: string | null; is_active: boolean }>)
      .filter((u) => !!normalize(u.name))
      .map((u) => ({
        id: u.id,
        name: normalize(u.name),
        is_active: !!u.is_active,
      }))
  },

  async listBrokersWithAssignments(): Promise<BrokerComercialGestaoItem[]> {
    const brokerRows = await fetchAllPages<BrokerRow>((from, to) =>
      supabase
        .from('brokers')
        .select('id, name, id_scgp, classification, state, city')
        .eq('status', 'A')
        .order('name')
        .range(from, to)
    )
    const brokerIds = brokerRows.map((b) => b.id)

    let assignmentsByBrokerId = new Map<string, BrokerComercialAssignment>()
    if (brokerIds.length > 0) {
      const assignments: BrokerComercialAssignment[] = []
      for (let i = 0; i < brokerIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = brokerIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: chunkData, error: assignmentsError } = await supabase
          .from('broker_comercial_assignments')
          .select('id, broker_id, comercial_user_id, assignment_type, rule_version, created_at, updated_at, updated_by')
          .in('broker_id', chunkIds)

        if (assignmentsError) throw assignmentsError
        assignments.push(...((chunkData || []) as BrokerComercialAssignment[]))
      }

      assignmentsByBrokerId = new Map(assignments.map((a) => [a.broker_id, a]))
    }

    const comercialUserIds = Array.from(
      new Set(
        Array.from(assignmentsByBrokerId.values())
          .map((a) => a.comercial_user_id)
          .filter(Boolean)
      )
    )

    const comercialNamesById = new Map<string, string>()
    if (comercialUserIds.length > 0) {
      for (let i = 0; i < comercialUserIds.length; i += IN_CHUNK_SIZE) {
        const chunkIds = comercialUserIds.slice(i, i + IN_CHUNK_SIZE)
        const { data: users, error: usersError } = await supabase
          .from('profiles')
          .select('id, name')
          .in('id', chunkIds)

        if (usersError) throw usersError

        ;((users || []) as Array<{ id: string; name: string | null }>).forEach((u) => {
          comercialNamesById.set(u.id, normalize(u.name))
        })
      }
    }

    return brokerRows.map((b) => {
      const assignment = assignmentsByBrokerId.get(b.id) || null
      return {
        id: b.id,
        name: b.name,
        id_scgp: b.id_scgp,
        classification: b.classification,
        state: b.state,
        city: b.city,
        assignment,
        comercial_name: assignment ? comercialNamesById.get(assignment.comercial_user_id) || null : null,
      }
    })
  },

  async listRules(): Promise<BrokerComercialRule[]> {
    const { data, error } = await supabase
      .from('broker_comercial_rules')
      .select('id, name, categories, states, is_fallback, priority, comercial_user_id, is_active, created_at, updated_at')
      .order('priority', { ascending: true })
      .order('created_at', { ascending: true })

    if (error) {
      if (isMissingRelationError(error)) return []
      throw error
    }
    return (data || []) as BrokerComercialRule[]
  },

  async saveRules(rules: BrokerComercialRuleInput[]): Promise<void> {
    const sanitized = rules.map((rule) => ({
      id: rule.id,
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c)).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }))

    const { error: deleteError } = await supabase
      .from('broker_comercial_rules')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000')
    if (deleteError) {
      if (isMissingRelationError(deleteError)) {
        throw new Error('Tabela de regras não encontrada. Execute o SQL comercial_05_gestao_regras.sql.')
      }
      throw deleteError
    }

    if (sanitized.length === 0) return

    const { error: insertError } = await supabase
      .from('broker_comercial_rules')
      .insert(sanitized)
    if (insertError) throw insertError
  },

  async createRule(rule: BrokerComercialRuleInput): Promise<void> {
    const payload = {
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }

    const { error } = await supabase
      .from('broker_comercial_rules')
      .insert(payload)

    if (error) throw error
  },

  async updateRule(rule: BrokerComercialRuleInput): Promise<void> {
    if (!rule.id) throw new Error('ID da regra é obrigatório para edição.')

    const payload = {
      name: normalize(rule.name) || 'Regra sem nome',
      categories: (rule.categories || []).map((c) => normalize(c).toUpperCase()).filter(Boolean),
      states: (rule.states || []).map((s) => normalize(s).toUpperCase()).filter(Boolean),
      is_fallback: !!rule.is_fallback,
      priority: Number.isFinite(rule.priority) ? Number(rule.priority) : 100,
      comercial_user_id: rule.comercial_user_id,
      is_active: !!rule.is_active,
    }

    const { error } = await supabase.from('broker_comercial_rules').update(payload).eq('id', rule.id)

    if (error) throw error
  },

  async deleteRule(ruleId: string): Promise<void> {
    const { error } = await supabase
      .from('broker_comercial_rules')
      .delete()
      .eq('id', ruleId)

    if (error) throw error
  },

  buildAutoAssignmentPreview(
    items: BrokerComercialGestaoItem[],
    rules: RuleLike[],
    comercialUsers: ComercialUser[]
  ): AutoAssignmentPreview {
    const preview: AutoAssignmentPreview = {
      totalEligible: 0,
      totalPendingManual: 0,
      totalIgnoredManual: 0,
      totalNoRuleMatch: 0,
      byComercial: [],
    }

    const byComercialMap = new Map<string, number>()
    const userNameById = new Map(comercialUsers.map((u) => [u.id, u.name]))
    const sortedRules = [...rules].sort((a, b) => a.priority - b.priority)

    items.forEach((item) => {
      const manualOnlyCategory = isManualOnlyCategory(item.classification)
      const hasManualAssignment = item.assignment?.assignment_type === 'manual'

      // Start/Basic não entram em distribuição automática; só são pendentes
      // quando ainda não têm responsável atribuído.
      if (manualOnlyCategory) {
        preview.totalPendingManual += 1
        return
      }

      // Ignorados por já estarem manuais (dentro do universo automático).
      if (hasManualAssignment) {
        preview.totalIgnoredManual += 1
        return
      }

      const matchedRule = getMatchedRule(item, sortedRules)
      if (!matchedRule) {
        preview.totalNoRuleMatch += 1
        return
      }

      preview.totalEligible += 1
      const comercialName = userNameById.get(matchedRule.comercial_user_id) || 'Comercial sem nome'
      byComercialMap.set(comercialName, (byComercialMap.get(comercialName) || 0) + 1)
    })

    preview.byComercial = Array.from(byComercialMap.entries()).map(([comercialName, count]) => ({
      comercialName,
      count,
    }))

    return preview
  },

  async applyAutoAssignments(
    items: BrokerComercialGestaoItem[],
    rules: RuleLike[]
  ): Promise<{ updated: number; skipped: number }> {
    const { data: authData } = await supabase.auth.getUser()
    const currentUserId = authData.user?.id || null
    const sortedRules = [...rules].filter((r) => r.is_active).sort((a, b) => a.priority - b.priority)

    const upserts: Array<{
      broker_id: string
      comercial_user_id: string
      assignment_type: 'auto'
      rule_version: string
      updated_by: string | null
    }> = []

    let skipped = 0
    for (const item of items) {
      if (item.assignment?.assignment_type === 'manual') {
        skipped += 1
        continue
      }
      if (isManualOnlyCategory(item.classification)) {
        skipped += 1
        continue
      }
      const matchedRule = getMatchedRule(item, sortedRules)
      if (!matchedRule) {
        skipped += 1
        continue
      }

      upserts.push({
        broker_id: item.id,
        comercial_user_id: matchedRule.comercial_user_id,
        assignment_type: 'auto',
        rule_version: RULE_VERSION,
        updated_by: currentUserId,
      })
    }

    if (upserts.length === 0) {
      return { updated: 0, skipped }
    }

    const chunkSize = 500
    for (let i = 0; i < upserts.length; i += chunkSize) {
      const chunk = upserts.slice(i, i + chunkSize)
      const { error } = await supabase
        .from('broker_comercial_assignments')
        .upsert(chunk, { onConflict: 'broker_id' })

      if (error) throw error
    }

    return { updated: upserts.length, skipped }
  },

  async assignManual(payload: ManualAssignmentPayload): Promise<void> {
    const brokerIds = Array.from(new Set(payload.brokerIds.filter(Boolean)))
    if (brokerIds.length === 0) return

    const { data: authData } = await supabase.auth.getUser()
    const currentUserId = authData.user?.id || null
    const rows = brokerIds.map((brokerId) => ({
      broker_id: brokerId,
      comercial_user_id: payload.comercialUserId,
      assignment_type: 'manual' as const,
      rule_version: null,
      updated_by: currentUserId,
    }))

    const { error } = await supabase
      .from('broker_comercial_assignments')
      .upsert(rows, { onConflict: 'broker_id' })

    if (error) throw error
  },

  /** Remove a linha de vínculo (corretor fica sem responsável comercial atribuído). */
  async removeBrokerComercialAssignment(brokerId: string): Promise<void> {
    if (!brokerId) return
    const { error } = await supabase.from('broker_comercial_assignments').delete().eq('broker_id', brokerId)
    if (error) throw error
  },
}
